"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Stan swiezego anti-spam tokenu (HMAC) pobranego z backendu Imperial.
 *
 * `ready === true` wtedy i tylko wtedy gdy hook dostal swiezy token
 * z endpointu `/api/iiapi/antispam/mint`. Formularz powinien blokowac
 * submit dopoki `ready === false` (przycisk disabled) - inaczej wysle
 * `token/timestamp` = null i backend rzuci 1002 (invalid token).
 *
 * `error` powietrzniejsze zeby caller mogl pokazac banner "sprobuj
 * odswiezyc strone" - fallback do server SSR tokenu byłby regresja do
 * problemu z 1004 (stale cache), wiec nie robimy fallbacku.
 */
export type AntiSpamTokenState = {
  token: string | null;
  timestamp: number | null;
  /** Hook zakonczyl fetch (sukces LUB blad) - submit gate. */
  ready: boolean;
  /** Human-readable komunikat bledu (fetch/network/backend). */
  error: string | null;
  /** Wymus ponowny mint (np. po submicie zeby dostac nowy token na kolejny). */
  refresh: () => void;
};

/**
 * Swiezy per-mount anti-spam token dla publicznych formularzy CMS.
 *
 * **Problem:** SSR zwraca `antispam.token`/`antispam.timestamp` w `getApiContent`
 * ktore jest cache'owane przez Next.js Data Cache (`revalidate: 60` +
 * stale-while-revalidate). W dev/low-traffic srodowisku timestamp moze byc
 * serwowany z cache przez 30+ min - backend rzuca 1004 (`age > 1800`).
 *
 * **Rozwiazanie:** hook wola `/api/iiapi/antispam/mint?scope=X|Y` z
 * `cache: 'no-store'` na mount. Backend zwraca swiezy `{token, timestamp}`
 * podpisany HMAC secret'em serwera. Frontend uzywa tych wartosci przy submicie
 * zamiast tych z SSR.
 *
 * **Scope musi pasowac do backend'owego `verifyToken`:**
 *   - `w_call`/`w_contact_form` -> `[id_window]`
 *   - `c_contact_form`/`c_registeration_to_newsletters` -> `[id_catalog]`
 *   - `c_category_advertisement` -> `[id_catalog, id_advert]`
 *
 * **StrictMode-safe:** guard'ujemy `didRunRef` zeby StrictMode double-invoke
 * effect'u nie wolal fetch'a 2x na mount (kazdy fetch to nowy token, drugi
 * bylby wywalony przez rate-limit gdyby endpoint mint mial rate-limit).
 *
 * @param scope Lista IDs (int lub string) identyfikujaca zasob formularza.
 *              Empty array = hook nie fetch'uje (`ready` stays false).
 * @returns {@see AntiSpamTokenState}
 */
export function useAntiSpamToken(scope: readonly (string | number)[]): AntiSpamTokenState {
  const [token, setToken] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  /* Serializuj scope do stable key zeby useEffect zna'l kiedy re-fetchowac.
     JSON.stringify jest deterministyczny dla prostych int/string arrays. */
  const scopeKey = scope.join("|");

  useEffect(() => {
    if (scopeKey === "") {
      /* Empty scope = caller nie potrzebuje tokenu (np. zalogowany user
         w newsletter form pomija anti-spam). Wchodzimy w `ready` zeby
         submit button nie zawisl w loading state. */
      setReady(true);
      setToken(null);
      setTimestamp(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setReady(false);
    setError(null);

    fetch(`/api/iiapi/antispam/mint?scope=${encodeURIComponent(scopeKey)}`, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (res) => {
        const json = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          token?: string;
          timestamp?: number;
          message?: string;
          error?: string;
        };
        if (cancelled) return;

        if (!res.ok || !json.ok || !json.token || !json.timestamp) {
          setError(json.message || json.error || `HTTP ${res.status}`);
          setToken(null);
          setTimestamp(null);
        } else {
          setToken(json.token);
          setTimestamp(json.timestamp);
          setError(null);
        }
        setReady(true);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        /* Abort z cleanup'em to nie blad - kolejny mount przyjdzie z nowym fetch'em. */
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : String(e));
        setReady(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [scopeKey, refreshTick]);

  const refreshRef = useRef(() => setRefreshTick((n) => n + 1));

  return {
    token,
    timestamp,
    ready,
    error,
    refresh: refreshRef.current,
  };
}
